import { X, TrendingUp, TrendingDown } from 'lucide-react'
import { PropertyResponse } from '@/services/api'
import { Button } from './Button'
import { getRenovationCosts, calculateMonthlyPayment, safeNumber } from '@/utils/propertyCalculations'

interface PropertyComparisonProps {
  properties: PropertyResponse[]
  onClose: () => void
  onRemoveProperty: (propertyId: string) => void
}

export function PropertyComparison({ properties, onClose, onRemoveProperty }: PropertyComparisonProps) {
  if (properties.length === 0) return null

  const formatCurrency = (amount: number) => `$${amount.toLocaleString()}`
  const formatPercent = (percent: number) => `${percent.toFixed(1)}%`

  const calculateMetrics = (property: PropertyResponse) => {
    const monthlyMortgage = calculateMonthlyPayment(
      safeNumber(property.loan_amount),
      safeNumber(property.interest_rate),
      safeNumber(property.loan_term, 30)
    )

    const monthlyExpenses = monthlyMortgage +
      (safeNumber(property.annual_taxes) / 12) +
      (safeNumber(property.annual_insurance) / 12) +
      safeNumber(property.monthly_hoa_fees)

    const monthlyCashFlow = safeNumber(property.expected_monthly_rent) - monthlyExpenses
    const annualCashFlow = monthlyCashFlow * 12
    const downPayment = safeNumber(property.down_payment)
    const cashOnCashReturn = downPayment > 0
      ? (annualCashFlow / downPayment) * 100
      : 0

    const totalRenovation = getRenovationCosts(property.renovation_costs)
    const purchasePrice = safeNumber(property.purchase_price)
    const totalInvestment = purchasePrice + totalRenovation

    // Cap rate calculation
    const noi = annualCashFlow + (monthlyMortgage * 12) // Add back mortgage payment for NOI
    const capRate = purchasePrice > 0 ? (noi / purchasePrice) * 100 : 0

    return {
      monthlyMortgage,
      monthlyExpenses,
      monthlyCashFlow,
      annualCashFlow,
      cashOnCashReturn,
      totalRenovation,
      totalInvestment,
      capRate,
    }
  }

  const metrics = properties.map(property => ({
    property,
    ...calculateMetrics(property)
  }))

  const comparisonRows = [
    {
      label: 'Purchase Price',
      getValue: (metric: any) => formatCurrency(metric.property.purchase_price),
      isNumeric: true,
      key: 'purchase_price'
    },
    {
      label: 'Down Payment',
      getValue: (metric: any) => formatCurrency(metric.property.down_payment),
      isNumeric: true,
      key: 'down_payment'
    },
    {
      label: 'Renovation Costs',
      getValue: (metric: any) => formatCurrency(metric.totalRenovation),
      isNumeric: true,
      key: 'totalRenovation'
    },
    {
      label: 'Total Investment',
      getValue: (metric: any) => formatCurrency(metric.totalInvestment),
      isNumeric: true,
      key: 'totalInvestment'
    },
    {
      label: 'Monthly Rent',
      getValue: (metric: any) => formatCurrency(metric.property.expected_monthly_rent || 0),
      isNumeric: true,
      key: 'expected_monthly_rent'
    },
    {
      label: 'Monthly Cash Flow',
      getValue: (metric: any) => formatCurrency(metric.monthlyCashFlow),
      isNumeric: true,
      key: 'monthlyCashFlow'
    },
    {
      label: 'Annual Cash Flow',
      getValue: (metric: any) => formatCurrency(metric.annualCashFlow),
      isNumeric: true,
      key: 'annualCashFlow'
    },
    {
      label: 'Cash-on-Cash Return',
      getValue: (metric: any) => formatPercent(metric.cashOnCashReturn),
      isNumeric: true,
      key: 'cashOnCashReturn'
    },
    {
      label: 'Cap Rate',
      getValue: (metric: any) => formatPercent(metric.capRate),
      isNumeric: true,
      key: 'capRate'
    },
  ]

  const getBestWorstIndicator = (row: any, index: number) => {
    if (!row.isNumeric || metrics.length < 2) return null
    
    const values = metrics.map(metric => {
      switch (row.key) {
        case 'purchase_price':
          return metric.property.purchase_price
        case 'down_payment':
          return metric.property.down_payment
        case 'totalRenovation':
          return metric.totalRenovation
        case 'totalInvestment':
          return metric.totalInvestment
        case 'expected_monthly_rent':
          return metric.property.expected_monthly_rent || 0
        case 'monthlyCashFlow':
        case 'annualCashFlow':
        case 'cashOnCashReturn':
        case 'capRate':
          return metric[row.key as keyof typeof metric] as number
        default:
          return 0
      }
    })

    const maxValue = Math.max(...values)
    const minValue = Math.min(...values)
    const currentValue = values[index]

    // For costs, lower is better
    const isCostMetric = ['purchase_price', 'down_payment', 'totalRenovation', 'totalInvestment'].includes(row.key)
    
    if (currentValue === maxValue && currentValue === minValue) return null // All same
    
    if (isCostMetric) {
      if (currentValue === minValue) return <TrendingUp className="w-4 h-4 text-green-500" />
      if (currentValue === maxValue) return <TrendingDown className="w-4 h-4 text-red-500" />
    } else {
      if (currentValue === maxValue) return <TrendingUp className="w-4 h-4 text-green-500" />
      if (currentValue === minValue) return <TrendingDown className="w-4 h-4 text-red-500" />
    }
    
    return null
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Property Comparison</h2>
          <Button variant="secondary" onClick={onClose} className="p-2">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Comparison Table */}
        <div className="p-6 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Metric</th>
                {properties.map((property) => (
                  <th key={property.property_id} className="text-left py-3 px-4 min-w-48">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-gray-900 text-sm">
                          {property.property_address}
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => onRemoveProperty(property.property_id)}
                          className="p-1 text-gray-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="text-xs text-gray-500">
                        {property.city}, {property.state}
                      </div>
                      <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {property.analysis_status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, rowIndex) => (
                <tr key={row.label} className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="py-3 px-4 font-medium text-gray-900">
                    {row.label}
                  </td>
                  {metrics.map((metric, index) => (
                    <td key={metric.property.property_id} className="py-3 px-4">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-900">
                          {row.getValue(metric)}
                        </span>
                        {getBestWorstIndicator(row, index)}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="px-6 pb-6">
          <div className="flex items-center space-x-6 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <TrendingUp className="w-4 h-4 text-green-500" />
              <span>Best Performance</span>
            </div>
            <div className="flex items-center space-x-1">
              <TrendingDown className="w-4 h-4 text-red-500" />
              <span>Needs Attention</span>
            </div>
            <div className="text-xs">
              * For costs, lower is better. For returns, higher is better.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper function to calculate monthly mortgage payment
